import {
  useEffect,
  useCallback,
} from 'react';
import {
  useNavigate,
} from 'react-router';
import {
  selection,
} from 'redux-resource-plugins';
import i18n from 'i18next';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  usePageView,
} from 'web-hooks';

import {
  Button,
  Select,
  Tooltip,
  Spinner,
  Checkbox,
  AddFolder,
  EditFolder,
  Breadcrumbs,
  FileUploader,
  ContentWrapper,
} from 'web-components';

import {
  Flex,
  Text,
  icons,
  SVG,
} from 'web-styled';

import Item from './Item';
import * as S from './styled';

const tooltipText = {
  folder: 'Create a new folder by clicking this button',
};

type Props = {
  folderId: number;
};

const ProDocuments = ({ folderId }: Props) => {
  const navigate = useNavigate();
  const resourceType = 'documents';
  const mapState = useCallback(state => ({
    items: coreSelectors.getChildrenFolderById(
      resourceType,
      Number(folderId),
    )(state),
    folder: coreSelectors.getResourceById(
      'folders',
      folderId,
    )(state),
    breadcrumbs: coreSelectors.getBreadcrumbs(
      resourceType,
      folderId,
    )(state),
    searchOptions: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
    ...getUiState([
      'fileUpload',
      'openedTooltips',
      'addFolderModal',
      'uploadFileModal',
      'renameFolderModal',
    ])(state),
  }), [folderId]);

  const {
    items,
    folder,
    fileUpload,
    breadcrumbs,
    searchOptions,
    openedTooltips,
    addFolderModal,
    uploadFileModal,
    renameFolderModal,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  // clear after leaving the page
  useEffect(() => () => {
    dispatch(
      selection.clearSelectedResources('folders'),
    );
    dispatch(
      selection.clearSelectedResources(resourceType),
    );
  }, []);

  usePageView({
    eventKey: 'viewed_folder',
    attrs: folder,
    enabled: !!folder?.id,
  });

  const handleAddItem = () => {
    dispatch(
      uiActions.setUiState({
        fileUpload: {},
        uploadFileModal: {
          isOpen: true,
        },
      }),
    );
  };

  const handleSelect = (item: any) => {
    const type = item.parent_id ? 'folders' : resourceType;
    dispatch(item.selected
      ? selection.deselectResources(type, [item.id])
      : selection.selectResources(type, [item.id]));
  };

  // items can contain folders and documents,
  // so we can't dispatch it's together
  const handleSelectAll = () => {
    const notRoot = items.filter(item => !item.is_root);
    notRoot.forEach(item => handleSelect(item));
  };

  const handleClickItem = (item: any) => {
    if (openedTooltips.some(el => el.key === item.name)) {
      const newState = {
        openedTooltips: openedTooltips.filter(el => el.key !== item.name),
      };
      dispatch(
        uiActions.setUiState(newState),
      );
    }
  };

  const handleChangeName = (item: any, newName: string) => {
    const type = item.parent_id ? 'folders' : resourceType;
    dispatch(
      coreActions.updateResources({
        resources: [{
          id: item.id,
          name: newName,
        }],
        resourceType: type,
      }),
    );
    dispatch(
      coreActions.requestUpdateEntity({
        id: item.id,
        payload: {
          name: newName,
        },
        endNotify: true,
        resourceType: type,
      }),
    );
  };

  const handleDelete = () => {
    const onConfirmAction = coreActions.requestDeleteItems({
      resourceType: 'documents',
      resources: items.filter(item => item.selected).map(item => item.id),
    });
    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete document(s)?',
          bodyText: 'This will delete the selected documents and unassign them from all clients.',
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  const handleDeleteFolder = () => {
    const onConfirm = () => {
      navigate(-1);
      dispatch(
        coreActions.requestDeleteEntity({
          id: folderId,
          resourceType: 'folders',
        }),
      );
    };

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete folder?',
          bodyText: 'Are you sure you want to delete this folder?',
          confirmText: 'Delete',
        },
        onConfirm,
      }),
    );
  };

  if (!folder) return <Spinner message="Loading Folder..." />;

  return (
    <ContentWrapper
      header={(
        <Flex
          column
          fillSpace
          spacing={10}
          padding="10px 0"
        >
          <Flex width="100%" align="center" justify="space-between">
            <Flex align="center" spacing={10}>
              {items.length > 0 && (
                <Checkbox
                  checked={items.every(item => item.selected)}
                  onChange={handleSelectAll}
                />
              )}
              <Breadcrumbs
                items={breadcrumbs}
                basePath="/documents"
                backPath="/documents_folder"
                parentId={folder.parent_id}
                hideBackChevron={folder.is_root}
              />
              <Button
                icon={<SVG.Plus size={14} strokeWidth={4} />}
                appearance="success"
                onClick={handleAddItem}
              >
                Add Document
              </Button>
            </Flex>
            <Flex align="center" spacing={10}>
              {breadcrumbs.length > 1 && (
                <Button
                  ghost
                  appearance="primary"
                  icon={<SVG.PencilIcon size={13} />}
                  onClick={() => {
                    dispatch(
                      uiActions.setUiState({
                        renameFolderModal: true,
                      }),
                    );
                  }}
                >
                  Edit folder name
                </Button>
              )}
              <Tooltip
                body={tooltipText.folder}
                positions={['left']}
              >
                <Button
                  ghost
                  appearance="primary"
                  icon={<SVG.Plus size={14} strokeWidth={4} />}
                  onClick={() => {
                    dispatch(
                      uiActions.setUiState({
                        addFolderModal: true,
                      }),
                    );
                  }}
                >
                  Add folder
                </Button>
              </Tooltip>
              {items.some(item => item.selected) && (
                <>
                  <Button
                    ghost
                    appearance="primary"
                    onClick={() => {
                      const onSelect = (values: any, actions: any) => {
                        dispatch(
                          coreActions.requestMoveToFolder({
                            items: items.filter(item => item.selected),
                            targetId: values.folderId,
                            resourceType,
                            callbacks: {
                              ...actions,
                              onSuccess: () => {
                                dispatch(
                                  uiActions.calcelMoveEntityModal(),
                                );
                              },
                            },
                          }),
                        );
                      };

                      dispatch(
                        uiActions.moveEntityModal({
                          modal: {
                            helper: i18n.t('select.folder.to.move.selected', { type: 'document' }),
                            isOpen: true,
                            folderName: 'Documents',
                          },
                          onSelect,
                        }),
                      );
                    }}
                  >
                    Move to
                  </Button>
                  <Button
                    ghost
                    appearance="danger"
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
          <Select
            value=""
            options={searchOptions}
            placeholder="Search Documents"
            styles={{
              container: provided => ({
                ...provided,
                width: '100%',
              }),
              menuList: provided => ({
                ...provided,
                maxHeight: '316px',
              }),
              control: provided => ({
                ...provided,
                boxShadow: 'none',
                borderColor: 'hsl(0,0%,80%)',
              }),
            }}
            onChange={(item) => {
              if (item?.value) {
                navigate(`/documents/${item.value}`);
              }
            }}
          />
        </Flex>
      )}
    >
      <Flex
        column
        fillSpace
      >
        {items.length ? items.map(item => (
          <Item
            key={item.id}
            isFolder={!!item.parent_id}
            item={item}
            openedTooltips={openedTooltips}
            onSelect={() => handleSelect(item)}
            onClickItem={handleClickItem}
            onChangeName={handleChangeName}
          />
        )) : (
          <S.Empty>
            <img src={icons.folder} alt="" />
            <Text color="#ccc">
              Nothing to show
            </Text>
            <Flex align="stretch" spacing={10} column>
              <Button
                ghost
                appearance="primary"
                icon={<SVG.Plus size={14} strokeWidth={4} />}
                onClick={handleAddItem}
              >
                Add Document
              </Button>
              <Button
                ghost
                appearance="danger"
                onClick={handleDeleteFolder}
              >
                Delete Folder
              </Button>
            </Flex>
          </S.Empty>
        )}
      </Flex>
      <FileUploader
        title="Upload documents"
        accept="application/pdf"
        isOpen={uploadFileModal.isOpen}
        isLoaded={uploadFileModal.isLoaded}
        helpText={i18n.t('uploading.document.trainer.help')}
        fileUpload={fileUpload}
        resourceType="documents"
        placeholder={(
          <span>
            Drop your
            {' '}
            <b>PDF</b>
            {' '}
            files here or
          </span>
        )}
        onRequestDrop={(files) => {
          dispatch(
            uiActions.requestUploadDocuments({
              files,
              folderId,
            }),
          );
        }}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              fileUpload: {},
              uploadFileModal: {
                isOpen: false,
              },
            }),
          );
        }}
        onRequestCancel={() => {
          dispatch(uiActions.cancelUploadFiles());
        }}
      />

      <AddFolder
        isOpen={addFolderModal}
        onRequestClose={() => {
          dispatch(uiActions.setUiState({ addFolderModal: false }));
        }}
        onSubmit={(values, actions) => {
          dispatch(
            coreActions.requestAddFolder({
              payload: {
                ...values,
                parent_id: folderId,
              },
              callbacks: {
                ...actions,
                onSuccess: () => {
                  dispatch(
                    uiActions.setUiState({
                      addFolderModal: false,
                    }),
                  );
                },
              },
            }),
          );
        }}
      />
      <EditFolder
        initialValues={{
          name: folder?.name || '',
        }}
        isOpen={renameFolderModal}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              renameFolderModal: false,
            }),
          );
        }}
        onSubmit={(values, actions) => {
          dispatch(
            coreActions.requestUpdateFolder({
              id: folderId,
              payload: values,
              callbacks: {
                ...actions,
                onSuccess: () => {
                  dispatch(
                    uiActions.setUiState({
                      renameFolderModal: false,
                    }),
                  );
                },
              },
            }),
          );
        }}
      />
    </ContentWrapper>
  );
};

export default ProDocuments;
